<template>
  <NuxtLayout name="default">
    <section class="near-me">
      <translate tag="h1"> Book Campsites and Camping Near Me </translate>
      <translate tag="h2" class="subhead">
        Use our campsites near me tool to search for sites by proximity to where
        you are now.
      </translate>
      <span class="page-content">
        <base-submit-button
          class="primary button near-me-btn"
          :disabled="geolocationError"
          :submitting="isGeolocationLoading"
          @click="geolocationMachine.send('LOCATE')"
        >
          <template #submittingText>
            <translate>Fetching your location...</translate>
          </template>
          <img
            src="@/assets/images/map-pin.svg"
            width="41"
            height="41"
            alt=""
          />
          <translate>Show campsites near me</translate>
        </base-submit-button>

        <geolocation-error
          v-if="isGeolocationFailed"
          :geolocation-error="geolocationError"
        />

        <div class="near-me-img-wrapper camping">
          <img
            src="@/assets/near-me/near-me.jpg"
            srcset="
              ~/assets/near-me/near-me.jpg        720w,
              ~/assets/near-me/near-me-large.jpg 2000w
            "
            :alt="$t('Camping near me open now')"
            width="360"
            height="270"
            fetchpriority="high"
            decoding="sync"
          />
        </div>

        <translate tag="p" :tt="{ within: defaultDistance }">
          Find camping sites near me using the button above, and granting us
          permission to use your location. We'll show you campsites that are
          open now within {within, distance, long} (as the crow flies - check
          the actual distance depending on your mode of transport).
        </translate>
        <translate tag="p">
          Filter and sort your results using more than 80 criteria to find
          touring caravan parks near me, static caravan parks near me, dog
          friendly campsites near me or campsites near me with swimming.
        </translate>
      </span>

      <section class="near-me-page-links">
        <translate tag="h2"> Other popular searches </translate>

        <nuxt-link :to="$puLocalePath({ name: 'glamping-near-me' })">
          <translate>Glamping near me</translate>
        </nuxt-link>

        <nuxt-link :to="$puLocalePath({ name: 'caravans-near-me' })">
          <translate>Caravan parks near me</translate>
        </nuxt-link>

        <nuxt-link :to="$puLocalePath({ name: 'tourers-near-me' })">
          <translate>Touring caravan sites near me</translate>
        </nuxt-link>

        <nuxt-link :to="$puLocalePath({ name: 'lodges-near-me' })">
          <translate>Lodges near me</translate>
        </nuxt-link>
      </section>

      <nav class="breadcrumbs">
        <span class="breadcrumbs-generator">
          <a
            :href="`https://${$config.public.envUrl}/${
              $i18n.locale !== 'en-gb' ? $i18n.locale + '/' : ''
            }`"
          >
            <translate>Home</translate> </a
          ><span class="crumb">&rsaquo;</span>
          <a
            :href="`https://${$config.public.envUrl}/${
              $i18n.locale !== 'en-gb' ? $i18n.locale + '/' : ''
            }campsites/`"
          >
            <translate>Campsites</translate> </a
          ><span class="crumb">&rsaquo;</span>
          <translate>Campsites near me</translate>
        </span>
      </nav>
      <nuxt-to-meta :nuxt2meta="head" />
    </section>
  </NuxtLayout>
</template>

<script setup lang="ts">
import generateNearMeUrl from '~/utility/generateNearMeUrl'
import { Distance } from '~/utility/geo/Distance'
import {
  GeolocationContext,
  GeolocationState,
  getGeolocationMachine,
} from '~/utility/geolocation/machine'
import SCHEMA_ORGANIZATION from '../../json/schemaOrganization.json'
import { computed, ref, watch, onMounted } from 'vue'

definePageMeta({
  layout: false,
})

const imageGlob = import.meta.glob<{ default: string }>(
  '@/assets/near-me/*.jpg',
  {
    query: '?url',
    eager: true,
  },
)

// Get Nuxt app context
const { $t, $config, $puLocalePath, $i18n } = useNuxtApp()
const route = useRoute()
const router = useRouter()

// Geolocation machine state
const geolocationMachine = ref<ReturnType<typeof getGeolocationMachine>>()
const geolocationContext = ref<GeolocationContext | null>(null)
const geolocationState = ref<GeolocationState | null>(null)

// Computed properties
const _breadcrumbs = computed(() => [{ label: $t('Campsites near me') }])

const defaultDistance = computed(() =>
  Distance.new({ mi: $config.public.defaultNearMeWithinRange }),
)

const isGeolocationFailed = computed(
  () => geolocationState.value?.value === 'error',
)

const isGeolocationLoading = computed(
  () => geolocationState.value?.value === 'locating',
)

const geolocationError = computed(() => geolocationContext.value?.errorStatus)

const head = computed(() => ({
  title: $t('Camping Sites Near Me') + ' | Pitchup.com',
  meta: [
    {
      hid: 'description',
      name: 'description',
      property: 'og:description',
      content: $t(
        'Find and book a campsite near you. With over 80 filters from pitch types (tent, caravan or motorhomes) to site facilities, luxury, family or dog friendly, you will find your perfect campsite on Pitchup.',
      ) as string,
    },
  ],
  link: [
    {
      rel: 'preload',
      as: 'image',
      href: imageGlob['/assets/near-me/near-me.jpg'].default,
      imagesrcset: `${imageGlob['/assets/near-me/near-me.jpg'].default} 720w, ${imageGlob['/assets/near-me/near-me-large.jpg'].default} 2000w`,
      imagesizes: '100vw',
      fetchpriority: 'high',
    },
  ],
  script: [
    {
      type: 'application/ld+json',
      body: true,
      json: {
        '@context': 'https://schema.org',
        '@type': 'ImageObject',
        name: $t('Camping near me') as string,
        contentUrl: `https://${$config.public.envUrl}${imageGlob['/assets/near-me/near-me-large.jpg'].default}`,
      },
    },
    {
      type: 'application/ld+json',
      body: true,
      json: {
        '@context': 'https://schema.org',
        '@type': 'ImageObject',
        name: $t('Glamping near me') as string,
        contentUrl: `https://${$config.public.envUrl}${imageGlob['/assets/near-me/glamping-near-me-large.jpg'].default}`,
      },
    },
    {
      type: 'application/ld+json',
      body: true,
      json: SCHEMA_ORGANIZATION,
      hid: 'schema-organization',
    },
    breadcrumbList.value,
  ],
}))

const breadcrumbList = computed(() => {
  const json = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        item: {
          '@id': `https://${$config.public.envUrl}/${
            $i18n.locale !== 'en-gb' ? $i18n.locale + '/' : ''
          }`,
          name: 'Pitchup.com',
        },
      },
      {
        '@type': 'ListItem',
        position: 2,
        item: {
          '@id': `https://${$config.public.envUrl}/${
            $i18n.locale !== 'en-gb' ? $i18n.locale + '/' : ''
          }campsites/`,
          name: $t('Campsites') as string,
        },
      },
      {
        '@type': 'ListItem',
        position: 3,
        item: {
          '@id': `https://${$config.public.envUrl}${route.path}`,
          name: $t('Campsites near me') as string,
        },
      },
    ],
  }
  return {
    type: 'application/ld+json',
    body: true,
    json,
  }
})

// Functions
const startGeolocationMachine = () => {
  geolocationMachine.value = getGeolocationMachine()
    .onTransition((state) => {
      geolocationState.value = state
      geolocationContext.value = state.context
    })
    .start()
}

const redirectToSearch = () => {
  if (!geolocationContext.value?.point) {
    return
  }
  const url = generateNearMeUrl(
    'search',
    route.query,
    geolocationContext.value.point,
    $i18n.locale,
  )

  void router.push({
    path: $puLocalePath(url.path),
    query: url.query as Record<string, any>,
  })
}

// Lifecycle
onMounted(async () => {
  await startGeolocationMachine()
})

// Watchers
watch(geolocationState, (newState) => {
  if (newState?.value === 'success') {
    redirectToSearch()
  }
})
</script>
<style>
@import url('~/assets/css/near-me.css');
</style>
